import {HttpClient, HttpParams} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {environment} from "src/environments/environment";
import {ProgramOrEnablerRoadmapEvent, RoadmapGroupPhaseBar} from "../program/roadmap/program-roadmap.service";
import {VisitableRoadmapGroup} from "../common/roadmap.service";

export interface PortfolioRoadmapWorkstreamData {
  id: number;
  name: string;
  groups: Array<PortfolioRoadmapGroup>;
}


export interface PortfolioRoadmapGroup extends VisitableRoadmapGroup {
  id: number;
  name: string;
  startDate: number;
  endDate: number;
  initialStartDate: number;
  initialEndDate: number;
  readyForSitePlanned: number;
  readyForSiteRealized: number;
  readyForSiteInitialPlanned: number;
  firstUsagePlanned: number;
  firstUsageRealized: number;
  firstUsageInitialPlanned: number;
  fullValuePlanned: number;
  fullValueRealized: number;
  fullValueInitialPlanned: number;

  description: string;
  phaseBars: Array<RoadmapGroupPhaseBar>;
  productId?: number; // In case of a product group, for the direct link, edit product, etc
  enablerProjectId?: number; // In case of an enabler project group

  // In case of a product deployment group
  deploymentGroup?: boolean;
  productScopes?: any[];
  productName?: string;

  enablerId?: number; // Presence indicates the group belongs to an enabler project (used for groupType detection); no redirection is done in the domain roadmap
  events: Array<ProgramOrEnablerRoadmapEvent>;
  workstreamId: number;

  // Name of the program owning this group. There is no concept of "mono-product program" nor dependency in the
  // domain roadmap, so this is only used to let the "Program" filter show/hide individual groups within a row.
  programName: string;

  // For deployment groups
  asset: any;
  site: any;

  percentProgress: number;
  percentTarget: number;

  progressWeight: number; // Total weight of the progress (for the grand total calculation)
  targetWeight: number; // Total weight of the target (for the grand total calculation)

  children: PortfolioRoadmapGroup[];
  status: string;
  deploymentPlan: string;
  progress: number;

  delayedDivsEndDate?: number;
}

@Injectable({
  providedIn: "root",
})
export class PortfolioReviewService {
  private readonly apiUrl = `${environment.webProtocol}://${environment.serverUrl}/api/portfolio-review`;

  constructor(private readonly http: HttpClient) {
  }

  getRoadmapData(portfolioId: number): Observable<Array<PortfolioRoadmapWorkstreamData>> {
    let httpParams: HttpParams = new HttpParams().set('portfolioId', portfolioId.toString());
    return this.http.get<Array<PortfolioRoadmapWorkstreamData>>(`${this.apiUrl}/roadmap/data`, {params: httpParams});
  }

  saveStandardGroup(groupData: PortfolioRoadmapGroup, portfolioId: number): Observable<any> {
    let httpParams: HttpParams = new HttpParams().set('portfolioId', portfolioId.toString());
    return this.http.post(`${this.apiUrl}/roadmap/group/save-standard`, groupData, {params: httpParams});
  }

  savePortfolioRoadmapEvent(eventData: ProgramOrEnablerRoadmapEvent, portfolioId: number): Observable<any> {
    let httpParams: HttpParams = new HttpParams().set('portfolioId', portfolioId.toString());
    return this.http.post(`${this.apiUrl}/roadmap/event/save`, eventData, {params: httpParams});
  }

  deleteGroup(groupId: number): Observable<any> {
    let httpParams: HttpParams = new HttpParams().set('groupId', groupId.toString());
    return this.http.get(`${this.apiUrl}/roadmap/group/delete`, {params: httpParams});
  }

  deleteEvent(eventId: number, portfolioId: number): Observable<any> {
    let httpParams: HttpParams = new HttpParams().set('eventId', eventId.toString()).set('portfolioId', portfolioId.toString());
    return this.http.get(`${this.apiUrl}/roadmap/event/delete`, {params: httpParams});
  }


  getPipelineEvolution(portfolioId: number, startDate: string, endDate: string, productIds: Array<number>): Observable<any> {
    let httpParams = new HttpParams().set("portfolioId", portfolioId);
    if (startDate) httpParams = httpParams.set("startDate", startDate);
    if (endDate) httpParams = httpParams.set("endDate", endDate);
    if (productIds) {
      for (let id of productIds) {
        httpParams = httpParams.append("productIds", id.toString());
      }
    }
    return this.http.get<any>(`${this.apiUrl}/pipeline-evolution`, {params: httpParams});
  }

  getTeamConfidence(portfolioId: number, programId: number): Observable<any> {
    let httpParams = new HttpParams();
    if (portfolioId != null) httpParams = httpParams.set("portfolioId", portfolioId);
    if (programId != null) httpParams = httpParams.set("programId", programId);
    return this.http.get<any[]>(`${this.apiUrl}/team-confidence`, {params: httpParams});
  }

  changeTeamConfidence(portfolioId: number, programId: number, productName: string, assetName: string, quarter: string, confidence: string): Observable<any> {
    let httpParams = new HttpParams().set("productName", productName).set("quarter", quarter).set("confidence", confidence);
    if (portfolioId != null) httpParams = httpParams.set("portfolioId", portfolioId);
    if (programId != null) httpParams = httpParams.set("programId", programId);
    if (assetName != null) httpParams = httpParams.set("assetName", assetName);
    return this.http.get<any[]>(`${this.apiUrl}/change-team-confidence`, {params: httpParams});
  }

  saveTeamConfidenceComment(portfolioId: number, programId: number, productName: string, assetName: string, quarter: string, comment: string): Observable<any> {
    let httpParams = new HttpParams().set("productName", productName).set("quarter", quarter);
    if (portfolioId != null) httpParams = httpParams.set("portfolioId", portfolioId);
    if (programId != null) httpParams = httpParams.set("programId", programId);
    if (assetName != null) httpParams = httpParams.set("assetName", assetName);
    return this.http.post<any[]>(`${this.apiUrl}/save-team-confidence-comment`, comment, {params: httpParams});
  }

  getPortfolio(type: string, name: string): Observable<any> {
    let httpParams = new HttpParams().set("type", type).set("name", name);
    return this.http.get<any>(`${this.apiUrl}/portfolio`, {params: httpParams});
  }

  getDataPlatformDetails(portfolioId: number): Observable<any> {
    let httpParams = new HttpParams().set("portfolioId", portfolioId);
    return this.http.get<any>(`${this.apiUrl}/data-platform-details`, {params: httpParams});
  }

  saveDataPlatformDetails(dataPlatform: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/data-platform-details`, dataPlatform);
  }

  getAllPortfoliosByType(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/all-portfolios-by-type`);
  }

  getAllPortfolioForMonitoring(type: string): Observable<any> {
    let httpParams = new HttpParams().set("type", type);
    return this.http.get<any>(`${this.apiUrl}/portfolio-for-monitoring`, {params: httpParams});
  }

  getOldestHistoricDate(portfolioId: number): Observable<string> {
    let httpParams = new HttpParams().set("portfolioId", portfolioId);
    return this.http.get(`${this.apiUrl}/oldest-historic-date`, {params: httpParams, responseType: 'text'});
  }
}
